import { useState } from 'react';

interface DrawerEvent {
  id: string;
  timestamp: string;
  type: 'auto_open' | 'manual_open' | 'forced_open' | 'paid_in' | 'paid_out' | 'reconciliation';
  user: string;
  role: string;
  amount?: number;
  reason?: string;
  note?: string;
  transactionId?: string;
}

interface DrawerDetailsModalProps {
  onClose: () => void;
  drawerInfo: {
    store: string;
    lane: string;
    cashier: string;
    shift: string;
    status: 'ready' | 'open' | 'error' | 'locked';
    openingBalance: number;
    cashSales: number;
    cashRefunds: number;
    paidIn: number;
    paidOut: number;
    expectedBalance: number;
  };
  events: DrawerEvent[];
}

export default function DrawerDetailsModal({ onClose, drawerInfo, events }: DrawerDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<'summary' | 'events'>('summary');

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'auto_open': return 'ri-shopping-cart-line text-blue-600';
      case 'manual_open': return 'ri-hand-coin-line text-orange-600';
      case 'forced_open': return 'ri-error-warning-line text-red-600';
      case 'paid_in': return 'ri-arrow-down-circle-line text-green-600';
      case 'paid_out': return 'ri-arrow-up-circle-line text-red-600';
      case 'reconciliation': return 'ri-calculator-line text-purple-600';
      default: return 'ri-information-line text-gray-600';
    }
  };

  const getEventLabel = (type: string) => {
    switch (type) {
      case 'auto_open': return 'Auto Open';
      case 'manual_open': return 'Manual Open';
      case 'forced_open': return 'Forced Open';
      case 'paid_in': return 'Paid In';
      case 'paid_out': return 'Paid Out';
      case 'reconciliation': return 'Reconciliation';
      default: return type;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <i className="ri-safe-2-line text-xl text-gray-700"></i>
            <h2 className="text-lg font-semibold text-gray-900">Cash Drawer Details</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        <div className="border-b">
          <div className="flex gap-1 p-2">
            <button
              onClick={() => setActiveTab('summary')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'summary'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-dashboard-line mr-2"></i>
              Summary
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'events'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-history-line mr-2"></i>
              Event Log ({events.length})
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'summary' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-600 mb-1">Store</p>
                  <p className="text-sm font-semibold text-gray-900">{drawerInfo.store}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-600 mb-1">Lane</p>
                  <p className="text-sm font-semibold text-gray-900">{drawerInfo.lane}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-600 mb-1">Cashier</p>
                  <p className="text-sm font-semibold text-gray-900">{drawerInfo.cashier}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-600 mb-1">Shift</p>
                  <p className="text-sm font-semibold text-gray-900">{drawerInfo.shift}</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-6">
                <p className="text-sm text-teal-700 mb-2">Expected Cash Balance</p>
                <p className="text-4xl font-bold text-teal-900">${drawerInfo.expectedBalance.toFixed(2)}</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Cash Movement</h3>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Opening Balance:</span>
                  <span className="text-sm font-medium text-gray-900">${drawerInfo.openingBalance.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-green-600">+ Cash Sales:</span>
                  <span className="text-sm font-medium text-green-600">${drawerInfo.cashSales.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-red-600">- Cash Refunds:</span>
                  <span className="text-sm font-medium text-red-600">${drawerInfo.cashRefunds.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-green-600">+ Paid In:</span>
                  <span className="text-sm font-medium text-green-600">${drawerInfo.paidIn.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-red-600">- Paid Out:</span>
                  <span className="text-sm font-medium text-red-600">${drawerInfo.paidOut.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                  <span className="text-base font-semibold text-gray-900">Expected Balance:</span>
                  <span className="text-xl font-bold text-gray-900">${drawerInfo.expectedBalance.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'events' && (
            <div className="space-y-3">
              {events.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-history-line text-4xl text-gray-300 mb-3"></i>
                  <p className="text-sm text-gray-500">No events recorded yet</p>
                </div>
              ) : (
                events.map((event) => (
                  <div key={event.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full flex-shrink-0">
                        <i className={`${getEventIcon(event.type)} text-lg`}></i>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{getEventLabel(event.type)}</p>
                            <p className="text-xs text-gray-500">{event.timestamp}</p>
                          </div>
                          {event.amount !== undefined && (
                            <span className={`text-lg font-bold ${event.type === 'paid_in' ? 'text-green-600' : event.type === 'paid_out' ? 'text-red-600' : 'text-gray-900'}`}>
                              {event.type === 'paid_in' ? '+' : event.type === 'paid_out' ? '-' : ''}${Math.abs(event.amount).toFixed(2)}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-600 mb-2">
                          <span>
                            <i className="ri-user-line mr-1"></i>
                            {event.user}
                          </span>
                          <span>
                            <i className="ri-shield-user-line mr-1"></i>
                            {event.role}
                          </span>
                          {event.transactionId && (
                            <span>
                              <i className="ri-barcode-line mr-1"></i>
                              {event.transactionId}
                            </span>
                          )}
                        </div>
                        
                        {event.reason && (
                          <div className="bg-gray-50 rounded px-2 py-1 mb-1">
                            <p className="text-xs text-gray-700">
                              <span className="font-medium">Reason:</span> {event.reason}
                            </p>
                          </div>
                        )}
                        
                        {event.note && (
                          <div className="bg-blue-50 rounded px-2 py-1">
                            <p className="text-xs text-blue-700">
                              <span className="font-medium">Note:</span> {event.note}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
